import type {
  FormField,
  FormFieldValue,
  FormSection,
} from '@/screens/ReportCreation/reportCreation.types';
import {
  isFieldVisible,
  isValueFilled,
} from '@/screens/ReportCreation/reportCreation.utils';
import {
  buildDependencyValueMap,
  getConditionalDependencyNames,
} from './conditionalVisibility.utils';

export type FieldValueGetter = (fieldId: string) => FormFieldValue;

export type FormValueMap = Record<string, FormFieldValue>;

export const buildFieldValueMap = (
  fieldNames: string[],
  values: unknown[] | undefined,
): FormValueMap =>
  fieldNames.reduce<FormValueMap>(
    (acc, name, index) => ({
      ...acc,
      [name]: values?.[index] as FormFieldValue,
    }),
    {},
  );

export const getRequiredFieldIds = (sections: FormSection[]): string[] =>
  sections.reduce<string[]>((acc, section) => {
    section.fields.forEach((field: FormField) => {
      if (!field.required) {
        return;
      }

      if (field.type === 'location') {
        acc.push(`${field.id}.latitude`, `${field.id}.longitude`);
        return;
      }

      acc.push(field.id);
    });

    return acc;
  }, []);

const buildDependencyMapForSection = (
  section: FormSection,
  getValue: FieldValueGetter,
): Record<string, FormFieldValue> => {
  const dependencyNames = getConditionalDependencyNames([section]);
  const dependencyValues = dependencyNames.map((name) => getValue(name));
  return buildDependencyValueMap(dependencyNames, dependencyValues);
};

const isFieldMissing = (
  field: FormField,
  getValue: FieldValueGetter,
  dependencyValueMap: Record<string, FormFieldValue>,
): boolean => {
  if (!field.required || !isFieldVisible(field, dependencyValueMap)) {
    return false;
  }

  if (field.type === 'location') {
    const latitude = getValue(`${field.id}.latitude`);
    const longitude = getValue(`${field.id}.longitude`);
    return !isValueFilled(latitude) || !isValueFilled(longitude);
  }

  return !isValueFilled(getValue(field.id));
};

export const hasMissingRequiredFields = (
  sections: FormSection[],
  getValue: FieldValueGetter,
): boolean =>
  sections.some((section) => {
    const dependencyValueMap = buildDependencyMapForSection(section, getValue);
    return section.fields.some((field) =>
      isFieldMissing(field, getValue, dependencyValueMap),
    );
  });
